import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { Loader2 } from "lucide-react";
import { API } from "../../../App";

const money = (v) =>
  v == null ? "—" : `€${Number(v).toLocaleString("en-IE", {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Table = ({ rows, emptyText, testIdPrefix }) => {
  if (!rows.length) {
    return <p className="text-slate-500 text-sm py-3">{emptyText}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-500 uppercase tracking-wide text-[10px]">
            <th className="text-left py-1.5 pr-3">Date</th>
            <th className="text-left py-1.5 pr-3">Product</th>
            <th className="text-right py-1.5 pr-3">Qty</th>
            <th className="text-right py-1.5 pr-3">Cost</th>
            <th className="text-left py-1.5 pr-3">Technician</th>
            <th className="text-left py-1.5 pr-3">Job</th>
            <th className="text-left py-1.5">Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.log_id}-${r.unit_id}-${i}`}
                className="border-t border-slate-800"
                data-testid={`${testIdPrefix}-row-${i}`}>
              <td className="py-1.5 pr-3 text-slate-400 whitespace-nowrap">{r.date}</td>
              <td className="py-1.5 pr-3 text-white">{r.product_name || "—"}</td>
              <td className="py-1.5 pr-3 text-right text-slate-300 whitespace-nowrap">
                {r.quantity}{r.base_unit}
              </td>
              <td className="py-1.5 pr-3 text-right font-mono text-sky-300 whitespace-nowrap">
                {r.cost == null
                  ? <span className="text-amber-400" title="No unit cost recorded">€0.00*</span>
                  : money(r.cost)}
              </td>
              <td className="py-1.5 pr-3 text-slate-300">{r.technician_name || "—"}</td>
              <td className="py-1.5 pr-3 text-slate-400">
                {r.customer_name || r.booking_id || "—"}
              </td>
              <td className="py-1.5 text-slate-400">{r.note || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Drill-down behind the Finance "Internal use of stock" line.
 *
 * Reads the SAME `stock_usage_purpose_report` the P&L line comes from,
 * so the total here can never disagree with the figure on the page.
 * Nothing is booked: internal use is reported below net operating
 * profit and is NOT an overhead — the accountant decides the treatment.
 */
export const InternalStockUseDialog = ({ open, onClose, periodParams }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/accounting/internal-stock-use`,
                                { params: periodParams });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load internal stock use");
    } finally {
      setLoading(false);
    }
  }, [periodParams]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const internal = data?.internal_use;
  const goodwill = data?.goodwill;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl max-h-[85vh] overflow-y-auto"
                     data-testid="internal-use-dialog">
        <DialogHeader>
          <DialogTitle>Internal use of stock</DialogTitle>
          <DialogDescription className="text-slate-400">
            {data?.period?.label || "…"} — stock that left the shelf but was not
            a cost of any customer sale. Not booked as an overhead; your
            accountant decides the treatment.
          </DialogDescription>
        </DialogHeader>

        {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}

        {!loading && internal && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="rounded border border-sky-500/40 bg-sky-500/5 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Internal use</p>
                <p className="text-sm font-mono text-sky-300"
                   data-testid="internal-use-dialog-total">
                  {money(internal.total_cost)}
                </p>
              </div>
              <div className="rounded border border-slate-700 bg-slate-800/50 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Lines</p>
                <p className="text-sm font-mono text-white">{internal.line_count}</p>
              </div>
              <div className="rounded border border-slate-700 bg-slate-800/50 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Jobs</p>
                <p className="text-sm font-mono text-white">{internal.job_count}</p>
              </div>
              <div className="rounded border border-amber-500/40 bg-amber-500/5 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Goodwill</p>
                <p className="text-sm font-mono text-amber-300"
                   data-testid="goodwill-dialog-total">
                  {money(goodwill?.total_cost)}
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-sky-300 mb-1">
                Internal use — excluded from job cost and margin
              </h4>
              <Table rows={internal.lines} testIdPrefix="internal-use"
                     emptyText="No internal use of stock in this period." />
              {internal.uncosted_lines > 0 && (
                <p className="text-[11px] text-amber-400 mt-1">
                  * {internal.uncosted_lines} line(s) have no unit cost recorded
                  and are valued at €0.
                </p>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold text-amber-300 mb-1">
                Goodwill to customers — stays inside job cost
              </h4>
              <Table rows={goodwill?.lines || []} testIdPrefix="goodwill"
                     emptyText="No goodwill lines in this period." />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
